import { ApiType } from "@gymflow/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { staffQueryKeys } from "../staff/staffQueryKeys";
import { memberQueryKeys } from "./memberQueryKeys";

export function useMemberInvoice({
  api,
  memberId,
  clubId,
  invoiceNumber,
}: {
  api: ApiType;
  memberId?: string;
  clubId?: number;
  invoiceNumber?: string;
}) {
  const queryClient = useQueryClient();

  const adjustInvoiceMutation = useMutation({
    mutationFn: ({
      invoiceNumber,
      amount,
    }: {
      invoiceNumber: string;
      amount: number;
    }) => api.memberApi.adjustInvoice(invoiceNumber, amount),
    onSettled: async () => {
      if (!memberId) return;
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.invoices(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.details(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.highlightedInvoice(memberId),
      });

      if (clubId && invoiceNumber) {
        await queryClient.invalidateQueries({
          queryKey: staffQueryKeys.invoice(invoiceNumber),
        });
      }
    },
  });

  const collectPastDueInvoiceMutation = useMutation({
    mutationFn: ({
      invoiceNumber,
      paymentMethodId,
    }: {
      invoiceNumber: string;
      paymentMethodId: string;
    }) => api.memberApi.collectPastDueInvoice(invoiceNumber, paymentMethodId),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.invoices(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.details(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.highlightedInvoice(memberId),
      });
      if (clubId && invoiceNumber) {
        await queryClient.invalidateQueries({
          queryKey: staffQueryKeys.invoice(invoiceNumber),
        });
      }
    },
  });

  const markCollectedByCustomPaymentMutation = useMutation({
    mutationFn: ({
      invoiceNumber,
      customPaymentCategoryId,
    }: {
      invoiceNumber: string;
      customPaymentCategoryId: number;
    }) =>
      api.memberApi.markCollectedByCustomPayment(
        invoiceNumber,
        customPaymentCategoryId,
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.invoices(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.details(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.highlightedInvoice(memberId),
      });
      if (clubId && invoiceNumber) {
        await queryClient.invalidateQueries({
          queryKey: staffQueryKeys.invoice(invoiceNumber),
        });
      }
    },
  });

  const refundInvoiceMutation = useMutation({
    mutationFn: ({ invoiceNumber }: { invoiceNumber: string }) =>
      api.memberApi.refundInvoice(invoiceNumber),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.invoices(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.details(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.highlightedInvoice(memberId),
      });
      if (clubId && invoiceNumber) {
        await queryClient.invalidateQueries({
          queryKey: staffQueryKeys.invoice(invoiceNumber),
        });
      }
    },
  });

  const writeOffInvoiceMutation = useMutation({
    mutationFn: ({ invoiceNumber }: { invoiceNumber: string }) =>
      api.memberApi.writeOffInvoice(invoiceNumber),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.invoices(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.details(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.highlightedInvoice(memberId),
      });
      if (clubId && invoiceNumber) {
        await queryClient.invalidateQueries({
          queryKey: staffQueryKeys.invoice(invoiceNumber),
        });
      }
    },
  });
  const cancelAuthorizationMutation = useMutation({
    mutationFn: ({ invoiceNumber }: { invoiceNumber: string }) =>
      api.memberApi.cancelInvoicePaymentAuth(invoiceNumber),
    onSettled: async () => {
      if (!memberId) return;
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.invoices(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.details(memberId),
      });
      await queryClient.invalidateQueries({
        queryKey: memberQueryKeys.highlightedInvoice(memberId),
      });
      if (clubId && invoiceNumber) {
        await queryClient.invalidateQueries({
          queryKey: staffQueryKeys.invoice(invoiceNumber),
        });
      }
    },
  });

  return {
    refundInvoiceMutation,
    writeOffInvoiceMutation,
    cancelAuthorizationMutation,
    adjustInvoiceMutation,
    collectPastDueInvoiceMutation,
    markCollectedByCustomPaymentMutation,
  };
}
